import React, { useState, useEffect } from 'react';
import { useApiKeys } from '../contexts/ApiKeyContext';
import { useAuth } from '../contexts/AuthContext';
import { promptsService, historyService } from '../services/firebase/firestore';
import toast from 'react-hot-toast';
import {
  Film,
  Camera,
  Sparkles,
  Download,
  Layers,
  Palette,
  Clock,
  Music,
  ChevronDown,
  ChevronUp,
  FileText,
  Image,
  Video,
  Bot,
  Key,
  Wand2,
  AlertCircle,
  Save
} from 'lucide-react';
import {
  detectDocumentaryGenre,
  generateDocumentaryScenes,
  generateStoryboard as generateBasicStoryboard,
  DocumentaryScene,
  DocumentaryStyle
} from '../services/documentaryAnalyzer';
import {
  initializeDocumentaryAI,
  isDocumentaryAIReady,
  analyzeDocumentaryWithAI,
  generateCinematicScenes,
  optimizePromptForPlatform,
  generateStoryboard as generateAIStoryboard
} from '../services/documentaryAI';
import { 
  analyzeDocumentaryWithAIPro,
  generateStoryFromPromptPro,
  optimizePromptForPlatformPro 
} from '../services/documentaryAIPro';

const DocumentaryGenerator: React.FC = () => {
  const { getApiKeyForModel, isKeyConfigured } = useApiKeys();
  const { user, isAuthenticated, trackUsage, profile } = useAuth();
  const [story, setStory] = useState('');
  const [scenes, setScenes] = useState<DocumentaryScene[]>([]);
  const [style, setStyle] = useState<DocumentaryStyle | null>(null);
  const [platform, setPlatform] = useState<'midjourney' | 'veo' | 'universal'>('midjourney');
  const [expandedScenes, setExpandedScenes] = useState<Set<number>>(new Set());
  const [copied, setCopied] = useState<number | null>(null);
  const [showStoryboard, setShowStoryboard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [narrativeArc, setNarrativeArc] = useState('');
  const [visualTheme, setVisualTheme] = useState('');
  const [keyMoments, setKeyMoments] = useState<string[]>([]);
  
  // Check if user is Pro or has API keys
  const isPro = profile && ['pro', 'premium', 'enterprise'].includes(profile.subscription || 'free');
  const openAIKey = getApiKeyForModel('gpt4');
  const hasOpenAI = isPro || !!openAIKey;

  const exampleStories = {
    comic: "The hilarious misadventures of a clumsy scientist trying to invent a teleportation device. Each failed experiment leads to increasingly absurd situations. His cat becomes the unwitting test subject. The laboratory transforms into a chaotic playground of scientific mishaps.",
    scientific: "The groundbreaking discovery of quantum computing revolutionizes technology. Scientists work tirelessly in advanced laboratories. Complex algorithms solve previously impossible problems. The future of human knowledge expands exponentially.",
    historical: "The rise and fall of the Roman Empire spans centuries of human civilization. Great leaders emerge from the chaos of war. Architectural marvels stand as testaments to human ambition. The echoes of ancient Rome still influence modern society.",
    nature: "Deep in the Amazon rainforest, a hidden ecosystem thrives. Rare species interact in delicate balance. The canopy shields countless secrets from the outside world. Climate change threatens this pristine wilderness."
  };

  useEffect(() => {
    // Initialize with OpenAI key if available (not needed for Pro users)
    if (openAIKey && !openAIKey.includes('SYSTEM')) {
      initializeDocumentaryAI(openAIKey);
    }
  }, [openAIKey]);

  const handleGenerateScenes = async () => {
    if (!story.trim()) return;
    
    setLoading(true);
    
    try {
      if (hasOpenAI) {
        // Use Pro-aware AI generation
        const analysis = await analyzeDocumentaryWithAIPro(story, platform, isPro ? undefined : openAIKey);
        setStyle(analysis.style);
        setScenes(analysis.scenes);
        setNarrativeArc(analysis.narrativeArc);
        setVisualTheme(analysis.visualTheme);
        setKeyMoments(analysis.keyMoments);
      } else {
        // Fallback to basic generation
        const detectedStyle = detectDocumentaryGenre(story);
        setStyle(detectedStyle);
        const generatedScenes = generateDocumentaryScenes(story, detectedStyle, platform);
        setScenes(generatedScenes);
      }
      setExpandedScenes(new Set([1])); // Auto-expand first scene
    } catch (error) {
      console.error('Error generating scenes:', error);
      // Fallback to basic generation on error
      const detectedStyle = detectDocumentaryGenre(story);
      setStyle(detectedStyle);
      const generatedScenes = generateDocumentaryScenes(story, detectedStyle, platform);
      setScenes(generatedScenes);
      setExpandedScenes(new Set([1]));
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateWithAI = async () => {
    if (!story.trim() || !isDocumentaryAIReady()) return;
    
    setLoading(true);
    
    try {
      const cinematicScenes = await generateCinematicScenes(
        story,
        style?.genre || 'scientific',
        scenes.length || 5,
        platform
      );
      setScenes(cinematicScenes);
    } catch (error) {
      console.error('Error regenerating scenes:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSceneExpansion = (sceneNumber: number) => {
    const newExpanded = new Set(expandedScenes);
    if (newExpanded.has(sceneNumber)) {
      newExpanded.delete(sceneNumber);
    } else {
      newExpanded.add(sceneNumber);
    }
    setExpandedScenes(newExpanded);
  };

  const copyPrompt = (text: string, sceneNumber: number) => {
    navigator.clipboard.writeText(text);
    setCopied(sceneNumber);
    toast.success('📋 Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  };
  
  const saveSceneToFirebase = async (scene: DocumentaryScene, sceneNumber: number) => {
    if (!isAuthenticated || !user) {
      toast.error('Please sign in to save scenes');
      return;
    }
    
    const savePromise = (async () => {
      const promptData = {
        userId: user.uid,
        title: `Documentary Scene ${sceneNumber}: ${scene.description.substring(0, 50)}...`,
        content: scene.imagePrompt,
        platform: platform,
        category: 'video' as const,
        tags: ['documentary', 'scene', style?.genre || 'general'],
        parameters: {
          videoPrompt: scene.videoPrompt,
          cameraWork: scene.cameraWork,
          mood: scene.mood,
          soundscape: scene.soundscape
        },
        isPublic: false,
        isFavorite: false
      };
      
      const promptId = await promptsService.create(promptData);
      
      // Track usage
      await trackUsage('promptsSaved');
      
      // Add to history
      await historyService.add({
        userId: user.uid,
        action: 'saved',
        content: scene.imagePrompt,
        platform: platform
      });
      
      return promptId;
    })();
    
    toast.promise(
      savePromise,
      {
        loading: 'Saving documentary scene...',
        success: '✅ Scene saved to dashboard!',
        error: (err) => `Failed to save: ${err.message || err}`
      }
    );
  };

  const extractCoreVisualPrompt = (fullPrompt: string): string => {
    // Extract the main visual description before technical parameters
    // Remove Midjourney parameters (--ar, --v, etc.)
    let corePrompt = fullPrompt.replace(/--\w+\s+[^\s]+/g, '').trim();
    // Remove trailing commas and clean up
    corePrompt = corePrompt.replace(/,\s*$/, '').trim();
    return corePrompt;
  };

  const downloadStoryboard = async () => {
    let storyboardText = '';
    
    if (hasOpenAI && isDocumentaryAIReady()) {
      try {
        storyboardText = await generateAIStoryboard(scenes, story.substring(0, 50));
      } catch {
        storyboardText = generateBasicStoryboard(scenes);
      }
    } else {
      storyboardText = generateBasicStoryboard(scenes);
    }
    
    const element = document.createElement("a");
    const file = new Blob([storyboardText], { type: 'text/markdown' });
    element.href = URL.createObjectURL(file);
    element.download = `documentary_storyboard_${Date.now()}.md`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const downloadAllPrompts = () => {
    let allPrompts = `Documentary: ${story}\n\n`;
    allPrompts += `Style: ${style?.visualStyle}\n\n`;
    allPrompts += '=== IMAGE PROMPTS ===\n\n';
    scenes.forEach(scene => {
      allPrompts += `Scene ${scene.sceneNumber}:\n${scene.imagePrompt}\n\n`;
    });
    allPrompts += '\n=== VIDEO PROMPTS ===\n\n';
    scenes.forEach(scene => {
      allPrompts += `Scene ${scene.sceneNumber}:\n${scene.videoPrompt}\n\n`;
    });

    const element = document.createElement("a");
    const file = new Blob([allPrompts], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `documentary_prompts_${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 text-white rounded-xl p-6 mb-6 shadow-xl">
        <div className="flex items-center gap-3 mb-3">
          <Film className="w-10 h-10" />
          <h1 className="text-3xl font-bold">Storyboard Generator</h1>
        </div>
        <p className="text-lg opacity-90">
          Transform your story into visual storyboard with AI-optimized prompts for images and videos
        </p>
      </div>

      {/* Input Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Tell Your Story
          </label>
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value)}
            placeholder="Describe your story narrative... The AI will detect if it's comic, scientific, historical, or other genres and generate appropriate visual storyboard with image and video prompts."
            rows={6}
            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Example Stories */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Try an example:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(exampleStories).map(([genre, exampleStory]) => (
              <button
                key={genre}
                onClick={() => setStory(exampleStory)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-blue-100 text-sm rounded-full transition-colors capitalize"
              >
                {genre} Documentary
              </button>
            ))}
          </div>
        </div>

        {/* Platform Selection */}
        <div className="flex gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Platform
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => setPlatform('midjourney')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  platform === 'midjourney'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Midjourney
              </button>
              <button
                onClick={() => setPlatform('veo')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  platform === 'veo'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Google Veo
              </button>
              <button
                onClick={() => setPlatform('universal')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  platform === 'universal'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Universal
              </button>
            </div>
          </div>
        </div>

        {/* AI Enhancement Section */}
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-gray-900">AI-Powered Generation (GPT-4)</span>
              {hasOpenAI && (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
              )}
            </div>
          </div>
          
          {!hasOpenAI && (
            <p className="text-sm text-gray-600">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Please configure OpenAI API key in settings (key icon in header) to enable AI features for intelligent scene analysis, cinematic prompts, and narrative flow optimization.
            </p>
          )}
          
          {hasOpenAI && (
            <div className="text-sm text-gray-700">
              <p className="mb-2">✨ AI Features Active:</p>
              <ul className="space-y-1 text-xs ml-4">
                <li>• Intelligent genre detection and style adaptation</li>
                <li>• Professional cinematography recommendations</li>
                <li>• Narrative arc analysis and scene pacing</li>
                <li>• Platform-optimized prompt generation</li>
              </ul>
            </div>
          )}
        </div>

        <button
          onClick={handleGenerateScenes}
          disabled={!story.trim() || loading}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              {hasOpenAI && isDocumentaryAIReady() ? 'Analyzing with AI...' : 'Generating...'}
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              {hasOpenAI && isDocumentaryAIReady() ? 'Generate Storyboard with AI (GPT-4)' : 'Generate Storyboard'}
            </>
          )}
        </button>
      </div>

      {/* Style Detection Result */}
      {style && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-6 border-2 border-purple-200">
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Palette className="w-5 h-5 text-purple-600" />
            Detected Style: {style.genre ? style.genre.charAt(0).toUpperCase() + style.genre.slice(1) : 'Universal'} Documentary
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Visual Style:</span>
              <p className="text-gray-600">{style.visualStyle}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Color Palette:</span>
              <p className="text-gray-600">{style.colorPalette}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Camera Style:</span>
              <p className="text-gray-600">{style.cameraStyle}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Narrative Tone:</span>
              <p className="text-gray-600">{style.narrativeTone}</p>
            </div>
          </div>
          
          {/* AI Analysis Results - only show when using AI */}
          {hasOpenAI && isDocumentaryAIReady() && (narrativeArc || visualTheme || keyMoments.length > 0) && (
            <div className="mt-4 pt-4 border-t border-purple-200">
              {narrativeArc && (
                <div className="mb-3">
                  <h4 className="font-medium text-purple-900 mb-1">📖 Narrative Arc:</h4>
                  <p className="text-sm text-gray-700">{narrativeArc}</p>
                </div>
              )}
              {visualTheme && (
                <div className="mb-3">
                  <h4 className="font-medium text-purple-900 mb-1">🎨 Visual Theme:</h4>
                  <p className="text-sm text-gray-700">{visualTheme}</p>
                </div>
              )}
              {keyMoments.length > 0 && (
                <div>
                  <h4 className="font-medium text-purple-900 mb-1">🎬 Key Moments:</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {keyMoments.map((moment, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-purple-500">•</span>
                        <span>{moment}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Generated Storyboard */}
      {scenes.length > 0 && (
        <div className="space-y-4 mb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Generated Storyboard ({scenes.length} Scenes)
              {hasOpenAI && isDocumentaryAIReady() && (
                <span className="ml-2 text-sm font-normal text-purple-600">
                  (AI-Enhanced with GPT-4)
                </span>
              )}
            </h2>
            <div className="flex gap-2">
              {hasOpenAI && isDocumentaryAIReady() && (
                <button
                  onClick={handleRegenerateWithAI}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Wand2 className="w-4 h-4" />
                  Regenerate Cinematic
                </button>
              )}
              <button
                onClick={() => setShowStoryboard(!showStoryboard)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                {showStoryboard ? 'Hide' : 'Show'} Storyboard
              </button>
              <button
                onClick={downloadStoryboard}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download Storyboard
              </button>
              <button
                onClick={downloadAllPrompts}
                className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download All Prompts
              </button>
            </div>
          </div>

          {/* Storyboard View */}
          {showStoryboard && (
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
              <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                {generateBasicStoryboard(scenes)}
              </pre>
            </div>
          )}

          {/* Scene Cards */}
          {scenes.map((scene) => (
            <div
              key={scene.sceneNumber}
              className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-200 hover:border-blue-300 transition-all"
            >
              {/* Scene Header */}
              <div
                onClick={() => toggleSceneExpansion(scene.sceneNumber)}
                className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 cursor-pointer hover:from-blue-50 hover:to-purple-50 transition-all"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {scene.sceneNumber}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{scene.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{scene.description}</p>
                    </div>
                  </div>
                  {expandedScenes.has(scene.sceneNumber) ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedScenes.has(scene.sceneNumber) && (
                <div className="p-6 space-y-4 border-t border-gray-200">
                  {/* Scene Metadata */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span>{scene.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Camera className="w-4 h-4 text-gray-500" />
                      <span>Camera: {scene.cameraWork.split(',')[0]}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Layers className="w-4 h-4 text-gray-500" />
                      <span>Mood: {scene.mood}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Music className="w-4 h-4 text-gray-500" />
                      <span>Sound: {scene.soundscape.split(',')[0]}</span>
                    </div>
                  </div>

                  {/* Image Prompt */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                        <Image className="w-4 h-4" />
                        Image Prompt
                      </h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const corePrompt = extractCoreVisualPrompt(scene.imagePrompt);
                            copyPrompt(corePrompt, scene.sceneNumber * 10 - 1);
                          }}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === scene.sceneNumber * 10 - 1
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                        >
                          {copied === scene.sceneNumber * 10 - 1 ? '✓ Core' : 'Copy Core'}
                        </button>
                        <button
                          onClick={() => copyPrompt(scene.imagePrompt, scene.sceneNumber * 10)}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === scene.sceneNumber * 10
                              ? 'bg-green-600 text-white'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {copied === scene.sceneNumber * 10 ? '✓ Full' : 'Copy Full'}
                        </button>
                        <button
                          onClick={() => saveSceneToFirebase(scene, scene.sceneNumber)}
                          className="px-3 py-1 bg-purple-600 text-white hover:bg-purple-700 rounded text-sm transition-colors flex items-center gap-1"
                        >
                          <Save className="w-3 h-3" />
                          Save
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 font-mono bg-white p-3 rounded">
                      {scene.imagePrompt}
                    </p>
                    {scene.imagePrompt.includes('--') && (
                      <div className="mt-2 p-2 bg-blue-100 rounded">
                        <p className="text-xs text-blue-800">
                          <strong>Core Visual:</strong> {extractCoreVisualPrompt(scene.imagePrompt).substring(0, 100)}...
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Video Prompt */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Video Prompt
                      </h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const corePrompt = extractCoreVisualPrompt(scene.videoPrompt);
                            copyPrompt(corePrompt, scene.sceneNumber * 10 + 0.5);
                          }}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === scene.sceneNumber * 10 + 0.5
                              ? 'bg-green-600 text-white'
                              : 'bg-purple-500 text-white hover:bg-purple-600'
                          }`}
                        >
                          {copied === scene.sceneNumber * 10 + 0.5 ? '✓ Core' : 'Copy Core'}
                        </button>
                        <button
                          onClick={() => copyPrompt(scene.videoPrompt, scene.sceneNumber * 10 + 1)}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === scene.sceneNumber * 10 + 1
                              ? 'bg-green-600 text-white'
                              : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                        >
                          {copied === scene.sceneNumber * 10 + 1 ? '✓ Full' : 'Copy Full'}
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 font-mono bg-white p-3 rounded">
                      {scene.videoPrompt}
                    </p>
                    {(scene.videoPrompt.includes('seconds') || scene.videoPrompt.includes('4K') || scene.videoPrompt.includes('8K')) && (
                      <div className="mt-2 p-2 bg-purple-100 rounded">
                        <p className="text-xs text-purple-800">
                          <strong>Core Action:</strong> {extractCoreVisualPrompt(scene.videoPrompt).substring(0, 100)}...
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Scene Details with Individual Copy Buttons */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Core Elements (Click to copy individually)
                    </h4>
                    
                    <div className="grid md:grid-cols-2 gap-3">
                      {/* Camera Work */}
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-700 flex items-center gap-1">
                            <Camera className="w-3 h-3" />
                            Camera Work
                          </span>
                          <button
                            onClick={() => copyPrompt(scene.cameraWork, scene.sceneNumber * 100 + 1)}
                            className={`px-2 py-0.5 rounded text-xs transition-colors ${
                              copied === scene.sceneNumber * 100 + 1
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-600 text-white hover:bg-gray-700'
                            }`}
                          >
                            {copied === scene.sceneNumber * 100 + 1 ? '✓' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-gray-600 text-xs mt-1 font-mono">{scene.cameraWork}</p>
                      </div>

                      {/* Mood */}
                      <div className="bg-gradient-to-r from-gray-50 to-purple-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-700 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Mood
                          </span>
                          <button
                            onClick={() => copyPrompt(scene.mood, scene.sceneNumber * 100 + 2)}
                            className={`px-2 py-0.5 rounded text-xs transition-colors ${
                              copied === scene.sceneNumber * 100 + 2
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-600 text-white hover:bg-gray-700'
                            }`}
                          >
                            {copied === scene.sceneNumber * 100 + 2 ? '✓' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-gray-600 text-xs mt-1 font-mono">{scene.mood}</p>
                      </div>

                      {/* Soundscape */}
                      <div className="bg-gradient-to-r from-gray-50 to-green-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-700 flex items-center gap-1">
                            <Music className="w-3 h-3" />
                            Soundscape
                          </span>
                          <button
                            onClick={() => copyPrompt(scene.soundscape, scene.sceneNumber * 100 + 3)}
                            className={`px-2 py-0.5 rounded text-xs transition-colors ${
                              copied === scene.sceneNumber * 100 + 3
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-600 text-white hover:bg-gray-700'
                            }`}
                          >
                            {copied === scene.sceneNumber * 100 + 3 ? '✓' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-gray-600 text-xs mt-1 font-mono">{scene.soundscape}</p>
                      </div>

                      {/* Duration */}
                      <div className="bg-gradient-to-r from-gray-50 to-yellow-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-gray-700 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Duration
                          </span>
                          <button
                            onClick={() => copyPrompt(scene.duration, scene.sceneNumber * 100 + 4)}
                            className={`px-2 py-0.5 rounded text-xs transition-colors ${
                              copied === scene.sceneNumber * 100 + 4
                                ? 'bg-green-600 text-white'
                                : 'bg-gray-600 text-white hover:bg-gray-700'
                            }`}
                          >
                            {copied === scene.sceneNumber * 100 + 4 ? '✓' : 'Copy'}
                          </button>
                        </div>
                        <p className="text-gray-600 text-xs mt-1 font-mono">{scene.duration}</p>
                      </div>
                    </div>

                    {/* Combined Core Prompt */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 border border-orange-200">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-gray-900">
                          🎯 Combined Core Prompt
                        </span>
                        <button
                          onClick={() => {
                            const corePrompt = `Camera: ${scene.cameraWork}\nMood: ${scene.mood}\nSound: ${scene.soundscape}\nDuration: ${scene.duration}`;
                            copyPrompt(corePrompt, scene.sceneNumber * 100 + 5);
                          }}
                          className={`px-3 py-1 rounded text-sm transition-colors ${
                            copied === scene.sceneNumber * 100 + 5
                              ? 'bg-green-600 text-white'
                              : 'bg-orange-600 text-white hover:bg-orange-700'
                          }`}
                        >
                          {copied === scene.sceneNumber * 100 + 5 ? 'Copied!' : 'Copy All Core'}
                        </button>
                      </div>
                      <div className="text-xs text-gray-700 font-mono bg-white p-2 rounded">
                        <div>Camera: {scene.cameraWork}</div>
                        <div>Mood: {scene.mood}</div>
                        <div>Sound: {scene.soundscape}</div>
                        <div>Duration: {scene.duration}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border-2 border-yellow-200">
        <h3 className="text-lg font-semibold mb-3 text-orange-900">
          📝 Pro Tips for Documentary Prompts
        </h3>
        <ul className="space-y-2 text-sm text-gray-700">
          <li>• <strong>Be Specific:</strong> Include details about time period, location, and key subjects</li>
          <li>• <strong>Genre Keywords:</strong> Use words like "comic", "scientific", "historical" to set the tone</li>
          <li>• <strong>Scene Flow:</strong> Write your story in chronological order for better scene generation</li>
          <li>• <strong>Visual Details:</strong> Mention specific visual elements you want to highlight</li>
          <li>• <strong>Platform Optimization:</strong> Choose the right platform for your output needs</li>
        </ul>
      </div>
    </div>
  );
};

export default DocumentaryGenerator;